/**
 * Blog Post Editor Component
 *
 * Full-featured blog post editor with SEO scoring, content stats,
 * and publishing controls. Uses feature hooks for all data operations.
 *
 * @see src/features/blog/hooks for mutation hooks
 * @see src/features/blog/utils for SEO and content calculations
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContentEditor } from '@/components/ui/content-editor';
import { DateTimePicker } from '@/components/ui/date-picker';
import {
  Save,
  Eye,
  Send,
  Tag,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Import from feature layer
import { useCreateBlogPost, useUpdateBlogPost } from '../../hooks';
import type { BlogPost, BlogPostFormData } from '../../types';
import {
  generateSlug,
  calculateSEOScore,
  getSEOScoreColor,
  getSEOProgressColor,
  getContentStats,
} from '../../utils';

interface BlogPostEditorProps {
  post?: BlogPost;
  onSave?: (post: BlogPost) => void;
  onCancel?: () => void;
  onPreview?: (post: BlogPost) => void;
  defaultStatus?: BlogPost['status'];
}

export const BlogPostEditor: React.FC<BlogPostEditorProps> = ({
  post,
  onSave,
  onCancel,
  onPreview,
  defaultStatus = 'draft'
}) => {
  const isEditing = !!post;

  // Use feature hooks for mutations
  const createMutation = useCreateBlogPost();
  const updateMutation = useUpdateBlogPost();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [formData, setFormData] = useState<BlogPostFormData>({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    status: post?.status || defaultStatus,
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    featured_image: post?.featured_image || '',
    author: post?.author || 'Admin User',
    category: post?.category || '',
    tags: post?.tags || [],
    published_at: post?.published_at || '',
    scheduled_for: post?.scheduled_for || ''
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !isEditing) {
      const newSlug = generateSlug(formData.title);
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  }, [formData.title, isEditing]);

  // Auto-generate meta title if not set
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData(prev => ({ ...prev, meta_title: formData.title }));
    }
  }, [formData.title, formData.meta_title]);

  // Calculate SEO score using feature utility
  const seoScore = calculateSEOScore({
    title: formData.title,
    metaDescription: formData.meta_description,
    excerpt: formData.excerpt,
    content: formData.content,
    featuredImage: formData.featured_image,
    tags: formData.tags,
    slug: formData.slug,
  });

  // Calculate content stats using feature utility
  const contentStats = getContentStats(formData.content);

  const handleInputChange = (
    field: keyof BlogPostFormData,
    value: BlogPostFormData[keyof BlogPostFormData]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (formData.status === 'published' && !formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required for published posts';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (status?: BlogPostFormData['status']) => {
    const dataToSave = status ? { ...formData, status } : formData;

    if (!validateForm()) return;

    // Set published_at if publishing
    if (status === 'published' && !dataToSave.published_at) {
      dataToSave.published_at = new Date().toISOString();
    }

    if (isEditing && post) {
      updateMutation.mutate(
        { id: post.id, data: dataToSave },
        {
          onSuccess: (result) => {
            if (onSave) onSave(result);
          },
        }
      );
    } else {
      createMutation.mutate(dataToSave, {
        onSuccess: (result) => {
          if (onSave) onSave(result);
        },
      });
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview({
        ...formData,
        id: post?.id || 'preview',
        created_at: post?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        word_count: contentStats.wordCount,
        reading_time: contentStats.readingTime,
      } as BlogPost);
    }
  };

  const getSeoScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (score >= 60) return <Clock className="h-4 w-4 text-yellow-600" />;
    return <AlertCircle className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update your blog post content and settings.' : 'Write and publish a new blog post for your website.'}
          </p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button
            variant="outline"
            onClick={() => { void handleSave('draft'); }}
            disabled={isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button
            onClick={() => { void handleSave('published'); }}
            disabled={isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            Publish
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Post Content</CardTitle>
              <CardDescription>
                Create engaging content for your blog readers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Post Title *</Label>
                <Input
                  id="title"
                  placeholder="Enter an engaging post title..."
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={errors.title ? 'border-red-500' : ''}
                />
                {errors.title && <p className="text-sm text-red-600">{errors.title}</p>}
                <p className="text-xs text-muted-foreground">
                  {formData.title.length}/60 characters (recommended: 30-60)
                </p>
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <Input
                  id="slug"
                  placeholder="url-friendly-slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  className={errors.slug ? 'border-red-500' : ''}
                />
                {errors.slug && <p className="text-sm text-red-600">{errors.slug}</p>}
                <p className="text-xs text-muted-foreground">
                  URL: /blog/{formData.slug}
                </p>
              </div>

              {/* Excerpt */}
              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  placeholder="Write a compelling excerpt that summarizes your post..."
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  rows={3}
                  className={errors.excerpt ? 'border-red-500' : ''}
                />
                {errors.excerpt && <p className="text-sm text-red-600">{errors.excerpt}</p>}
                <p className="text-xs text-muted-foreground">
                  {formData.excerpt.length}/160 characters (shown in search results)
                </p>
              </div>

              {/* Content Editor */}
              <div className="space-y-2">
                <ContentEditor
                  label="Post Content *"
                  description="Write your blog post content with rich formatting, images, and media."
                  value={formData.content}
                  onChange={(content) => handleInputChange('content', content)}
                  placeholder="Start writing your blog post content..."
                  height={500}
                  showPreview={true}
                  showRawHtml={true}
                  error={errors.content}
                />
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader>
              <CardTitle>SEO Settings</CardTitle>
              <CardDescription>
                Optimize your post for search engines and social media.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Meta Title */}
              <div className="space-y-2">
                <Label htmlFor="meta_title">Meta Title</Label>
                <Input
                  id="meta_title"
                  placeholder="SEO title for search engines..."
                  value={formData.meta_title}
                  onChange={(e) => handleInputChange('meta_title', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.meta_title?.length || 0}/60 characters
                </p>
              </div>

              {/* Meta Description */}
              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  placeholder="Describe your post for search engine results..."
                  value={formData.meta_description}
                  onChange={(e) => handleInputChange('meta_description', e.target.value)}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.meta_description?.length || 0}/160 characters
                </p>
              </div>

              {/* Featured Image */}
              <div className="space-y-2">
                <Label htmlFor="featured_image">Featured Image URL</Label>
                <Input
                  id="featured_image"
                  placeholder="https://example.com/image.jpg"
                  value={formData.featured_image}
                  onChange={(e) => handleInputChange('featured_image', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Will be used as the main image for the post and social sharing
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publishing Options */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Author */}
              <div className="space-y-2">
                <Label>Author</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => handleInputChange('author', e.target.value)}
                  placeholder="Author name"
                />
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masonry-tips">Masonry Tips</SelectItem>
                    <SelectItem value="home-maintenance">Home Maintenance</SelectItem>
                    <SelectItem value="project-showcase">Project Showcase</SelectItem>
                    <SelectItem value="industry-news">Industry News</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule */}
              {formData.status === 'scheduled' && (
                <div className="space-y-2">
                  <Label>Scheduled For</Label>
                  <DateTimePicker
                    date={formData.scheduled_for ? new Date(formData.scheduled_for) : undefined}
                    onDateChange={(date) => {
                      if (date) {
                        const currentTime = formData.scheduled_for ? new Date(formData.scheduled_for).toTimeString().slice(0, 5) : '09:00';
                        const dateStr = date.toISOString().split('T')[0];
                        handleInputChange('scheduled_for', `${dateStr}T${currentTime}:00`);
                      } else {
                        handleInputChange('scheduled_for', '');
                      }
                    }}
                    time={formData.scheduled_for ? new Date(formData.scheduled_for).toTimeString().slice(0, 5) : '09:00'}
                    onTimeChange={(time) => {
                      if (formData.scheduled_for) {
                        const dateStr = new Date(formData.scheduled_for).toISOString().split('T')[0];
                        handleInputChange('scheduled_for', `${dateStr}T${time}:00`);
                      }
                    }}
                    placeholder="Select date and time"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
                <Button size="sm" onClick={handleAddTag}>
                  <Tag className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* SEO Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                {getSeoScoreIcon(seoScore.overall)}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Overall Score</span>
                    <span className={`font-bold ${getSEOScoreColor(seoScore.overall)}`}>
                      {seoScore.overall}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getSEOProgressColor(seoScore.overall)}`}
                      style={{ width: `${seoScore.overall}%` }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span>Title Length</span>
                  <span className={seoScore.titleLength ? 'text-green-600' : 'text-red-600'}>
                    {seoScore.titleLength ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Meta Description</span>
                  <span className={seoScore.metaDescription ? 'text-green-600' : 'text-red-600'}>
                    {seoScore.metaDescription ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Content Length</span>
                  <span className={seoScore.contentLength ? 'text-green-600' : 'text-red-600'}>
                    {seoScore.contentLength ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Featured Image</span>
                  <span className={seoScore.featuredImage ? 'text-green-600' : 'text-red-600'}>
                    {seoScore.featuredImage ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Tags (3+)</span>
                  <span className={seoScore.tags ? 'text-green-600' : 'text-red-600'}>
                    {seoScore.tags ? '✓' : '✗'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Content Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span>Words</span>
                <span className="font-medium">{contentStats.wordCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Characters</span>
                <span className="font-medium">{contentStats.characterCount}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Reading Time</span>
                <span className="font-medium">{contentStats.readingTime} min</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;
